// !!! IMPORTANT !!! This needs to be kept in sync with @hex/python-kernel-startup/python_kernel_startup/hex_mimetype.py
import { ExportType, FileUploadType } from "./enums.js";
import {
  ExportOutputContents,
  ExportOutputContentsFailure,
  ExportOutputContentsLegacy,
  ExportOutputContentsSuccess,
} from "./kernel-output-types/outputs.js";
import { TypedKernelFunctions } from "./shared-types/kernelInterfaceTypes.js";

// This is a "default" mimetype for binary files. Any binary files we have
// uploaded without a content type will have this default file type.
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types#applicationoctet-stream
export const APPLICATION_OCTET_STREAM = "application/octet-stream" as const;

// Google Drive MIME types
export const GOOGLE_APPS_FOLDER_MIMETYPE =
  "application/vnd.google-apps.folder" as const;
export const GOOGLE_APPS_SPREADSHEET_MIMETYPE =
  "application/vnd.google-apps.spreadsheet" as const;
export const GOOGLE_APPS_DOCUMENT_MIMETYPE =
  "application/vnd.google-apps.document" as const;

// Export mimetypes
export const HEX_MIMETYPE_PREFIX = "application/vnd.hex" as const;
// We no longer produce exports of this mimetype, but we did in the past
export const CSV_EXPORT_MIMETYPE = `${HEX_MIMETYPE_PREFIX}.export+csv` as const;
// Instead we now export data in the parquet format typically
export const PARQUET_EXPORT_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.export+parquet` as const;
// For data in the tablestore we produce an export that simply points to that data,
// which can be converted to CSV on-demand
export const TABLESTORE_EXPORT_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.export+tablestore` as const;

export const PREVIEW_QUERY_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.previewquery+json` as const;

export const CSV_DOWNLOAD_QUERY_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.csvdownloadquery+json` as const;

export const CSV_DOWNLOAD_SOURCE_QUERY_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.csvdownloadquery+json;download_mode=source` as const;

export const CSV_DOWNLOAD_TABLESTORE_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.csvdownloadtablestore+json` as const;

export const CSV_DOWNLOAD_SOURCE_TABLESTORE_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.csvdownloadtablestore+json;download_mode=source` as const;

// All mime types that should be routed through the export handler.
// This includes both the "export+*" family (file-based exports) and
// tablestore-backed types (which contain table store refs that need extraction).
const EXPORT_MIMETYPES_LIST = [
  CSV_EXPORT_MIMETYPE,
  PARQUET_EXPORT_MIMETYPE,
  TABLESTORE_EXPORT_MIMETYPE,
  CSV_DOWNLOAD_TABLESTORE_MIMETYPE,
  CSV_DOWNLOAD_SOURCE_TABLESTORE_MIMETYPE,
  PREVIEW_QUERY_MIMETYPE,
] as const;
export type ExportMimeType = (typeof EXPORT_MIMETYPES_LIST)[number];

export const EXPORT_MIMETYPES: ReadonlySet<string> = new Set(
  EXPORT_MIMETYPES_LIST,
);

/**
 * Query-mode CSV download mimetypes. These are metadata-only outputs that
 * contain warehouse query parameters — they don't carry tablestore refs and
 * must be redacted from app-viewer output contents.
 */
const QUERY_MODE_EXPORT_MIMETYPES_LIST = [
  CSV_DOWNLOAD_QUERY_MIMETYPE,
  CSV_DOWNLOAD_SOURCE_QUERY_MIMETYPE,
] as const;
export type QueryModeExportMimeType =
  (typeof QUERY_MODE_EXPORT_MIMETYPES_LIST)[number];

export const QUERY_MODE_EXPORT_MIMETYPES: ReadonlySet<string> = new Set(
  QUERY_MODE_EXPORT_MIMETYPES_LIST,
);

export const isExportMimeType = (
  mimeType: string,
): mimeType is ExportMimeType => {
  return EXPORT_MIMETYPES.has(mimeType);
};

export interface ExportTypeInfo {
  extension: string;
  hexMimeType: string;
  mimeType: string;
}

export const EXPORT_TYPES: Record<ExportType, ExportTypeInfo> = {
  csv: {
    extension: "csv",
    hexMimeType: CSV_EXPORT_MIMETYPE,
    mimeType: "text/csv",
  },
  parquet: {
    extension: "parquet",
    hexMimeType: PARQUET_EXPORT_MIMETYPE,
    mimeType: APPLICATION_OCTET_STREAM,
  },
  tablestore: {
    extension: "tablestore",
    hexMimeType: TABLESTORE_EXPORT_MIMETYPE,
    mimeType: APPLICATION_OCTET_STREAM,
  },
  preview: {
    extension: "tablestore",
    hexMimeType: PREVIEW_QUERY_MIMETYPE,
    mimeType: APPLICATION_OCTET_STREAM,
  },
};

export interface FileUploadTypeInfo {
  extension: string;
  mimetype: string;
  // These are defined in python-kernel-startup/python_kernel_startup/kernel_execution/input_cell.py
  getPythonCodeHandler: TypedKernelFunctions;
}

export const FILE_UPLOAD_TYPES: Record<FileUploadType, FileUploadTypeInfo> = {
  CSV: {
    extension: "csv",
    mimetype: "text/csv",
    getPythonCodeHandler: "csvToDataFrame",
  },
  EXCEL_OPENXML: {
    extension: "xlsx",
    mimetype:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    getPythonCodeHandler: "excelToDataFrame",
  },
  EXCEL_LEGACY: {
    extension: "xls",
    mimetype: "application/vnd.ms-excel",
    getPythonCodeHandler: "excelToDataFrame",
  },
};

const exportMimeTypes = Object.entries(EXPORT_TYPES).reduce<
  Record<string, ExportType>
>((map, [exportTypeKey, exportTypeValue]) => {
  map[exportTypeValue.hexMimeType] = exportTypeKey as ExportType;
  return map;
}, {});

export const getExportTypes = (
  outputContents: { mimeType: string }[],
): ExportType[] => {
  return outputContents
    .filter((outputContent) =>
      Object.keys(exportMimeTypes).includes(outputContent.mimeType),
    )
    .map((outputContent) => exportMimeTypes[outputContent.mimeType])
    .filter((exportType): exportType is ExportType => exportType != null);
};

export type ExportTypeAndStatus = {
  exportType: ExportType;
  exportFailureReason: string | null;
};
export const getExportTypesAndStatus = (
  outputContents: readonly { mimeType: string; contents: string }[],
): ExportTypeAndStatus[] => {
  return outputContents.flatMap((oc) => {
    const exportType = exportMimeTypes[oc.mimeType];
    if (exportType == null) {
      return [];
    }

    let exportContent: unknown;
    try {
      exportContent = JSON.parse(oc.contents);
    } catch {
      return [];
    }
    const parsed = ExportOutputContents.safeParse(exportContent);
    if (!parsed.success) {
      return [];
    }

    const failure = ExportOutputContentsFailure.safeParse(parsed.data);
    return [
      {
        exportType,
        exportFailureReason: failure.success ? failure.data.reason : null,
      },
    ];
  });
};

export const getExportKeyFromOutputContents = (
  outputContents: ExportOutputContents | undefined,
): string | undefined => {
  if (outputContents == null) {
    return undefined;
  }
  if (ExportOutputContentsLegacy.safeParse(outputContents).success) {
    return outputContents as string;
  }
  const success = ExportOutputContentsSuccess.safeParse(outputContents);
  return success.success ? success.data.exportKey : undefined;
};

// Metadata mimetypes
export const FILLED_DYNAMIC_VALUE_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.filleddynamicvalue+json` as const;
export const SQL_STATUS_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.sqlstatus+json` as const;
export const SQL_COMPILED_QUERY_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.sqlcompiledquery+json` as const;
export const WRITEBACK_STATUS_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.writebackstatus+json` as const;
export const PIVOT_TABLE_CONFIG_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.pivottableconfig+json` as const;
export const MAP_FILLED_DYNAMIC_VALUE_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.mapfilleddynamicvalue+json` as const;
export const COLUMN_AGGREGATIONS_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.columnaggregations+json` as const;
// Used to indicate there was a problem capturing scope
// Can indicate the we failed to capture a single value
// or that something broke w/ the comm
export const SCOPE_ERROR_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.scopeerror+json` as const;
// The state hydration prototype is based around persisting state to rehydrate
// in special metadata outputs of this type which are not sent to the client.
// This is somewhat of a hack but a necessary one as any alternative will
// require creating a new type of `TableReference` link to the state which will
// be an involved migration and out of scope for a prototype.
export const STATE_SLICE_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.stateslice+json` as const;
export const STATE_SLICE_SESSION_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.stateslicesession+json` as const;

export const HQL_QUERY_LOG_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.hqlquerylog+json` as const;

export const EXECUTION_EVENT_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.executionevent-do-not-store+json` as const;

export const isHiddenMimeType = (mimeType: string): boolean => {
  return (
    mimeType === STATE_SLICE_MIMETYPE ||
    mimeType === STATE_SLICE_SESSION_MIMETYPE
  );
};

export const METADATA_MIMETYPES = [
  FILLED_DYNAMIC_VALUE_MIMETYPE,
  MAP_FILLED_DYNAMIC_VALUE_MIMETYPE,
  SQL_STATUS_MIMETYPE,
  WRITEBACK_STATUS_MIMETYPE,
  PIVOT_TABLE_CONFIG_MIMETYPE,
  SQL_COMPILED_QUERY_MIMETYPE,
  SCOPE_ERROR_MIMETYPE,
  STATE_SLICE_MIMETYPE,
  STATE_SLICE_SESSION_MIMETYPE,
  ...QUERY_MODE_EXPORT_MIMETYPES_LIST,
  HQL_QUERY_LOG_MIMETYPE,
] as const;
export type MetadataMimeType = (typeof METADATA_MIMETYPES)[number];

export const isMetadataMimeType = (
  mimeType: string,
): mimeType is MetadataMimeType => {
  return METADATA_MIMETYPES.some((mt) => mt === mimeType);
};

// Custom hex display mimetypes
export const DISPLAY_TABLE_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.displaytable+json` as const;
export const OVERSIZE_OUTPUT_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.oversized-output+json` as const;
export const OUTPUTS_TRUNCATED_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.outputs-truncated+json` as const;
export const METRIC_CELL_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.metriccell+json` as const;
export const RICH_TEXT_CELL_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.richtext+json` as const;
// Mime-type for vega-fusion outputs. Represents a v5 Vega spec.
export const HEX_CHART_MIMETYPE = `${HEX_MIMETYPE_PREFIX}.chart+json` as const;
export const CUSTOM_ERROR_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.customerror+json` as const;
export const ARROW_TABLE_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.arrowtable+json` as const;
export const EXECUTION_LOG_MIMETYPE =
  `${HEX_MIMETYPE_PREFIX}.execution-log+json` as const;

// Custom mime-type for state transfer
export const HEX_STATE_MIMETYPE = `${HEX_MIMETYPE_PREFIX}.state+json` as const;

// Highest priority for rendering is on top
const DISPLAY_MIME_TYPE_PRIORITY = [
  CUSTOM_ERROR_MIMETYPE,
  "traceback",
  EXECUTION_LOG_MIMETYPE,
  OUTPUTS_TRUNCATED_MIMETYPE,
  DISPLAY_TABLE_MIMETYPE,
  METRIC_CELL_MIMETYPE,
  RICH_TEXT_CELL_MIMETYPE,
  HEX_CHART_MIMETYPE,
  ARROW_TABLE_MIMETYPE,
  // MimeTypes pulled from:
  // https://github.com/jupyterlab/jupyterlab/blob/master/packages/rendermime/src/factories.ts
  // https://github.com/jupyterlab/jupyterlab/blob/master/packages/vega5-extension/src/index.ts
  "application/vnd.plotly.v1+json",
  "application/vnd.vega.v2+json",
  "application/vnd.vega.v3+json",
  "application/vnd.vega.v4+json",
  "application/vnd.vega.v5+json",
  "application/vnd.vegalite.v1+json",
  "application/vnd.vegalite.v2+json",
  "application/vnd.vegalite.v3+json",
  "application/vnd.vegalite.v4+json",
  "application/vnd.vegalite.v5+json",
  "text/html",
  "image/bmp",
  "image/png",
  "image/jpeg",
  "image/gif",
  // "text/latex", // Hex doesn't support this currently
  "text/markdown",
  "image/svg+xml",
  // It's important that OVERSIZE_OUTPUT_MIMETYPE stays above text/plain so that getPreferredMimeType selects
  // the oversize output in cases of oversized outputs
  // More context: https://github.com/hex-inc/hex/pull/16219
  OVERSIZE_OUTPUT_MIMETYPE,
  "text/plain",
  // Hex doesn't support these for now
  // "application/vnd.jupyter.stdout",
  // "application/vnd.jupyter.stderr",
  // "text/javascript",
  // "application/javascript",
] as const;
export type DisplayMimeType = (typeof DISPLAY_MIME_TYPE_PRIORITY)[number];

export const isDisplayMimeType = (
  mimeType: string,
): mimeType is DisplayMimeType => {
  return DISPLAY_MIME_TYPE_PRIORITY.some((mt) => mt === mimeType);
};

// Compile-time check: display, metadata, and export mimetypes must be disjoint.
type _AssertNever<_T extends never> = true;
type _MimeTypeSetsDisjoint = _AssertNever<
  | (DisplayMimeType & MetadataMimeType)
  | (DisplayMimeType & ExportMimeType)
  | (MetadataMimeType & ExportMimeType)
>;

export const getMimeTypePriority = (mimeType: string): number => {
  if (isDisplayMimeType(mimeType)) {
    return DISPLAY_MIME_TYPE_PRIORITY.indexOf(mimeType);
  } else {
    return Number.MAX_SAFE_INTEGER;
  }
};

export const getPreferredMimeType = (
  mimeTypes: string[],
): string | undefined => {
  const orderedMimeTypes = mimeTypes.sort(
    (a, b) => getMimeTypePriority(a) - getMimeTypePriority(b),
  );
  return orderedMimeTypes[0];
};

const sanitizedMimeTypes: DisplayMimeType[] = [
  "text/plain",
  "text/markdown",
  OVERSIZE_OUTPUT_MIMETYPE,
  "traceback",
  CUSTOM_ERROR_MIMETYPE,
];

export const shouldSanitize = (mimeType: string): boolean => {
  return isDisplayMimeType(mimeType) && sanitizedMimeTypes.includes(mimeType);
};

export const ALLOWED_IMAGE_FILE_MIMETYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/svg+xml",
];

const ALLOWED_NON_IMAGE_FILE_MIMETYPES = ["text/csv", "text/plain"];

export const ALLOWED_PUBLIC_DOWNLOAD_FILE_MIMETYPES = [
  ...ALLOWED_IMAGE_FILE_MIMETYPES,
  ...ALLOWED_NON_IMAGE_FILE_MIMETYPES,
  APPLICATION_OCTET_STREAM,
];

export type MimeType = DisplayMimeType | MetadataMimeType | ExportMimeType;
